import React, { useState, useEffect, useRef } from 'react';
import ReactECharts from 'echarts-for-react';
import './EnergyFinancingChart.scss';

const EnergyFinancingChart = () => {
  const [activeFilter, setActiveFilter] = useState('Combined');
  const [loading, setLoading] = useState(false);
  const chartContainerRef = useRef(null);

  const filters = ['Combined', 'World Bank', 'AfDB'];

  // Sample data for different sources
  const chartData = {
    Combined: {
      renewableEnergy: [1.2, 1.8, 2.1, 2.5, 3.2, 4.1, 5.5],
      energyAccess: [0.8, 1.1, 1.3, 1.6, 2.0, 2.4, 3.0]
    },
    'World Bank': {
      renewableEnergy: [0.8, 1.2, 1.4, 1.7, 2.1, 2.6, 3.2],
      energyAccess: [0.6, 0.8, 0.9, 1.1, 1.4, 1.7, 2.1]
    },
    AfDB: {
      renewableEnergy: [0.4, 0.6, 0.7, 0.8, 1.1, 1.5, 2.3],
      energyAccess: [0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.9]
    }
  };

  const currentData = chartData[activeFilter];

  // Handle loading state on filter change for smooth transition
  useEffect(() => {
    setLoading(true);
    // Add minimum delay for smooth transition (300ms)
    const timer = setTimeout(() => {
      setLoading(false);
    }, 300);

    return () => clearTimeout(timer);
  }, [activeFilter]);

  // Prevent mouse wheel scrolling on chart container
  useEffect(() => {
    const chartContainer = chartContainerRef.current;
    if (!chartContainer) return;

    const handleWheel = (e) => {
      // Prevent page scroll when hovering over chart
      e.stopPropagation();
    };

    chartContainer.addEventListener('wheel', handleWheel, { passive: false });

    return () => {
      chartContainer.removeEventListener('wheel', handleWheel);
    };
  }, []);

  const chartOption = {
    title: {
      text: null
    },
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'shadow'
      },
      formatter: function(params) {
        let tooltip = '<b>' + params[0].axisValue + '</b><br/>';
        params.forEach(function(item) {
          tooltip += '<span style="color:' + item.color + '">●</span> ' + 
                    item.seriesName + ': $' + item.value + 'B<br/>';
        });
        tooltip += '<b>Source: ' + activeFilter + '</b>';
        return tooltip;
      }
    },
    legend: {
      data: ['Renewable Energy', 'Energy Access'],
      top: 10,
      textStyle: {
        fontSize: 12,
        fontFamily: 'Spartan, sans-serif'
      }
    },
    grid: {
      left: '3%',
      right: '4%',
      bottom: '3%',
      top: '15%',
      containLabel: true
    },
    xAxis: {
      type: 'category',
      data: ['2017', '2018', '2019', '2020', '2021', '2022', '2023'],
      axisLine: {
        lineStyle: {
          color: '#e5e5e5'
        }
      },
      axisTick: {
        show: false
      },
      axisLabel: {
        color: '#666',
        fontSize: 12,
        fontFamily: 'Spartan, sans-serif'
      }
    },
    yAxis: {
      type: 'value',
      name: 'Investment (Billion USD)',
      nameLocation: 'middle',
      nameGap: 50,
      min: 0,
      max: 6,
      interval: 1,
      axisLine: {
        lineStyle: {
          color: '#e5e5e5'
        }
      },
      axisTick: {
        show: false
      },
      axisLabel: {
        color: '#666',
        fontSize: 12,
        fontFamily: 'Spartan, sans-serif'
      },
      nameTextStyle: {
        color: '#666',
        fontSize: 12,
        fontFamily: 'Spartan, sans-serif'
      },
      splitLine: {
        lineStyle: {
          color: '#f0f0f0'
        }
      }
    },
    series: [
      {
        name: 'Renewable Energy',
        type: 'bar',
        data: currentData.renewableEnergy,
        itemStyle: {
          color: '#4CAF50'
        },
        barWidth: '25%'
      },
      {
        name: 'Energy Access',
        type: 'bar',
        data: currentData.energyAccess,
        itemStyle: {
          color: '#FF9800'
        },
        barWidth: '25%'
      }
    ]
  };

  return (
    <div className="energy-financing-chart">
      <div className="section-header">
        <h3 className="section-title">Energy Financing by Year</h3>
      </div>
      
      <div className="chart-filters">
        {filters.map(filter => (
          <button
            key={filter}
            className={`filter-btn ${activeFilter === filter ? 'active' : ''}`}
            onClick={() => setActiveFilter(filter)}
          >
            {filter}
          </button>
        ))}
      </div>
      
      <div className="chart-container" ref={chartContainerRef}>
        {loading ? (
          <div className="loading-container">
            <img 
              src="/spinner/oval-spinner.gif" 
              alt="Loading..." 
              className="chart-loader-spinner"
            />
          </div>
        ) : (
          <ReactECharts 
            option={chartOption} 
            style={{ height: '300px', width: '100%' }}
          />
        )}
      </div>
    </div>
  );
};

export default EnergyFinancingChart;
